import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../hooks';
import { ApexChartSeries } from '../types';
import { formatNumbers, goodFormatNumber } from '../utils';

dayjs.extend(utc);

type CanvasChartType = 'area' | 'line';

interface CanvasPoint {
  x: number;
  y: number;
}

interface PreparedPoint {
  x: number;
  y: number;
  rawX: string;
}

interface PreparedSeries {
  key: string;
  name?: string;
  currency?: string;
  color: string;
  data: PreparedPoint[];
}

interface HoverState {
  clientX: number;
  clientY: number;
  x: number;
  y: number;
  timestamp: number;
  values: {
    name?: string;
    currency?: string;
    color: string;
    value: number | null;
    point?: PreparedPoint | null;
  }[];
}

type SeriesOpacityMap = Record<string, number>;

interface BalanceChartWorkerResponse {
  requestId: number;
  preparedSeries?: PreparedSeries[];
  error?: string;
}

interface BalanceChartWorkerSeriesInput {
  name?: string;
  currency?: string;
  color?: string;
  data: {
    x: unknown;
    y: unknown;
  }[];
}

export interface BalanceChartCanvasProps {
  type?: CanvasChartType;
  fill?: boolean;
  data: ApexChartSeries[];
  height?: string;
  withDateTime?: boolean;
  rotateLabel?: number;
  countsLabel?: number;
  colors?: string[];
  className?: string;
  showAllMarkers?: boolean;
  showMarkers?: boolean;
  markerStrokeColor?: string;
}

const defaultColors = ['#0369A1'];
const emptyPreparedSeries: PreparedSeries[] = [];
const chartPadding = {
  top: 16,
  right: 16,
  bottom: 34,
  left: 56,
};
const maxVisibleMarkers = 600;
const legendToggleAnimationDuration = 240;

const resolveDate = (value: unknown) => {
  const stringValue = String(value);
  const dateTimeMatch = stringValue.match(/^(\d{2})-(\d{2})-(\d{4})(?: (\d{2}):(\d{2}):(\d{2}))?$/);

  if (dateTimeMatch) {
    const [, day, month, year, hours = '0', minutes = '0', seconds = '0'] = dateTimeMatch;
    return new Date(+year, +month - 1, +day, +hours, +minutes, +seconds).getTime();
  }

  const isoDateMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return new Date(+year, +month - 1, +day).getTime();
  }

  return new Date(value as string | number | Date).getTime();
};

const roundToThree = (value: number) => {
  const factor = 1000;
  const absoluteValue = Math.abs(value);
  const roundedValue = Math.round(absoluteValue * factor) / factor;

  return value < 0 ? -roundedValue : roundedValue;
};

const prepareSeries = (data: ApexChartSeries[], chartColors: string[]) =>
  data.map((item, index) => {
    const points = ((item.data as { x: unknown; y: number }[]) || []).reduce<PreparedPoint[]>(
      (result, point) => {
        const x = resolveDate(point.x);

        if (!Number.isFinite(x)) {
          return result;
        }

        result.push({
          rawX: String(point.x),
          x,
          y: roundToThree(Number(point.y) || 0),
        });

        return result;
      },
      [],
    );

    points.sort((a, b) => a.x - b.x);

    return {
      key: `${item.name || 'series'}_${index}`,
      name: item.name,
      currency: item.currency,
      color: item.color || chartColors[index % chartColors.length],
      data: points,
    };
  });

const toWorkerSeriesInput = (data: ApexChartSeries[]): BalanceChartWorkerSeriesInput[] =>
  data.map(item => ({
    name: item.name,
    currency: item.currency,
    color: item.color,
    data: item.data.map(point => ({
      x: point.x,
      y: point.y,
    })),
  }));

const drawText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: { align?: CanvasTextAlign; color?: string; rotate?: number } = {},
) => {
  ctx.save();
  ctx.fillStyle = options.color || '#A3A3A3';
  ctx.textAlign = options.align || 'left';
  ctx.font = '400 12px Inter, sans-serif';
  ctx.translate(x, y);

  if (options.rotate) {
    ctx.rotate((options.rotate * Math.PI) / 180);
  }

  ctx.fillText(text, 0, 0);
  ctx.restore();
};

const findNearestPoint = (points: PreparedPoint[], timestamp: number) => {
  if (!points.length) return null;

  let left = 0;
  let right = points.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (points[mid].x < timestamp) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  const current = points[left];
  const previous = points[left - 1];

  if (!previous) return current;

  return Math.abs(previous.x - timestamp) < Math.abs(current.x - timestamp) ? previous : current;
};

const compactPoints = (points: CanvasPoint[], maxPoints: number) => {
  if (points.length <= maxPoints || maxPoints <= 0) return points;

  const buckets = new Map<number, CanvasPoint[]>();

  points.forEach(point => {
    const key = Math.round(point.x);
    const bucket = buckets.get(key);

    if (bucket) {
      bucket.push(point);
    } else {
      buckets.set(key, [point]);
    }
  });

  return Array.from(buckets.values()).flatMap(bucket => {
    if (bucket.length <= 2) return bucket;

    const minY = bucket.reduce((result, point) => (point.y < result.y ? point : result), bucket[0]);
    const maxY = bucket.reduce((result, point) => (point.y > result.y ? point : result), bucket[0]);

    return minY.x <= maxY.x ? [minY, maxY] : [maxY, minY];
  });
};

export const BalanceChartCanvas = ({
  data,
  type = 'area',
  fill = true,
  height = '100%',
  withDateTime = false,
  rotateLabel = 0,
  countsLabel = 6,
  colors,
  className,
  showAllMarkers = true,
  showMarkers = true,
  markerStrokeColor,
}: BalanceChartCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isHoverRenderRef = useRef(false);
  const workerRef = useRef<Worker | null>(null);
  const workerRequestIdRef = useRef(0);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hover, setHover] = useState<HoverState | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<Set<string>>(new Set());
  const [seriesOpacity, setSeriesOpacity] = useState<SeriesOpacityMap>({});
  const [workerPreparedSeries, setWorkerPreparedSeries] = useState<PreparedSeries[] | null>(null);
  const [shouldUseWorker, setShouldUseWorker] = useState(() => typeof Worker !== 'undefined');
  const { isMobile } = useIsMobile();
  const chartColors = colors?.length ? colors : defaultColors;

  const syncPreparedSeries = useMemo<PreparedSeries[]>(
    () => (shouldUseWorker ? [] : prepareSeries(data, chartColors)),
    [chartColors, data, shouldUseWorker],
  );
  const preparedSeries = useMemo(
    () => (shouldUseWorker ? workerPreparedSeries ?? emptyPreparedSeries : syncPreparedSeries),
    [shouldUseWorker, syncPreparedSeries, workerPreparedSeries],
  );

  useEffect(() => {
    if (!shouldUseWorker) return;

    if (typeof Worker === 'undefined') {
      setShouldUseWorker(false);
      return;
    }

    workerRef.current ??= new Worker(new URL('./BalanceChartCanvas.worker.ts', import.meta.url), {
      type: 'module',
    });

    const requestId = workerRequestIdRef.current + 1;
    workerRequestIdRef.current = requestId;

    workerRef.current.onmessage = (event: MessageEvent<BalanceChartWorkerResponse>) => {
      if (event.data.requestId !== workerRequestIdRef.current) return;

      if (event.data.error || !event.data.preparedSeries) {
        setShouldUseWorker(false);
        return;
      }

      setWorkerPreparedSeries(event.data.preparedSeries);
    };

    workerRef.current.onerror = () => {
      setShouldUseWorker(false);
    };

    try {
      workerRef.current.postMessage({
        requestId,
        data: toWorkerSeriesInput(data),
        colors: chartColors,
      });
    } catch {
      setShouldUseWorker(false);
    }
  }, [chartColors, data, shouldUseWorker]);

  useEffect(
    () => () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    },
    [],
  );

  const visibleSeries = useMemo(
    () =>
      preparedSeries.filter(series => {
        const opacity = seriesOpacity[series.key] ?? 1;
        return !hiddenSeriesKeys.has(series.key) || opacity > 0;
      }),
    [hiddenSeriesKeys, preparedSeries, seriesOpacity],
  );

  const bounds = useMemo(() => {
    let minX = Infinity;
    let maxX = -Infinity;
    let maxValue = 0;

    visibleSeries.forEach(series => {
      series.data.forEach(point => {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y > maxValue) maxValue = point.y;
      });
    });

    const maxY = maxValue + maxValue * 0.1 || 1;

    return {
      minX: Number.isFinite(minX) ? minX : 0,
      maxX: Number.isFinite(maxX) ? maxX : 1,
      minY: 0,
      maxY,
    };
  }, [visibleSeries]);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const updateSize = () => {
      setSize({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsVisible(false);

    const animationFrameId = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [preparedSeries]);

  useEffect(() => {
    setHiddenSeriesKeys(currentKeys => {
      const availableKeys = new Set(preparedSeries.map(series => series.key));
      const nextKeys = new Set(
        Array.from(currentKeys).filter(seriesKey => availableKeys.has(seriesKey)),
      );

      return nextKeys.size === currentKeys.size ? currentKeys : nextKeys;
    });
    setSeriesOpacity(currentOpacity => {
      const availableKeys = new Set(preparedSeries.map(series => series.key));
      return Object.fromEntries(
        Object.entries(currentOpacity).filter(([seriesKey]) => availableKeys.has(seriesKey)),
      );
    });
  }, [preparedSeries]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.width || !size.height) return;

    const ratio = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    isHoverRenderRef.current = false;

    canvas.width = size.width * ratio;
    canvas.height = size.height * ratio;
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const plotWidth = Math.max(size.width - chartPadding.left - chartPadding.right, 1);
    const plotHeight = Math.max(size.height - chartPadding.top - chartPadding.bottom, 1);
    const xRange = Math.max(bounds.maxX - bounds.minX, 1);
    const yRange = Math.max(bounds.maxY - bounds.minY, 1);
    const xScale = (value: number) =>
      chartPadding.left + ((value - bounds.minX) / xRange) * plotWidth;
    const yScale = (value: number) =>
      chartPadding.top + plotHeight - ((value - bounds.minY) / yRange) * plotHeight;

    ctx.lineWidth = 1;
    ctx.strokeStyle = '#E5E7EB';

    for (let index = 0; index <= 4; index += 1) {
      const y = chartPadding.top + (plotHeight / 4) * index;
      ctx.beginPath();
      ctx.moveTo(chartPadding.left, y);
      ctx.lineTo(size.width - chartPadding.right, y);
      ctx.stroke();

      const value = bounds.maxY - (bounds.maxY / 4) * index;
      drawText(ctx, value ? formatNumbers(value) : '0', chartPadding.left - 10, y + 4, {
        align: 'right',
      });
    }

    ctx.beginPath();
    ctx.moveTo(chartPadding.left, chartPadding.top + plotHeight);
    ctx.lineTo(size.width - chartPadding.right, chartPadding.top + plotHeight);
    ctx.stroke();

    const labelCount = Math.max(countsLabel, 2);
    for (let index = 0; index < labelCount; index += 1) {
      const ratio = labelCount === 1 ? 0 : index / (labelCount - 1);
      const timestamp = bounds.minX + xRange * ratio;
      const x = chartPadding.left + plotWidth * ratio;
      const label = dayjs.utc(timestamp).format('DD-MM');

      drawText(ctx, label, x, size.height - 12, {
        align: 'center',
        rotate: isMobile ? -60 : rotateLabel,
      });
    }

    visibleSeries.forEach(series => {
      const opacity = hiddenSeriesKeys.has(series.key) ? seriesOpacity[series.key] ?? 0 : 1;
      if (opacity <= 0) return;

      const rawScreenPoints: CanvasPoint[] = [];

      series.data.forEach(point => {
        rawScreenPoints.push({
          x: xScale(point.x),
          y: yScale(point.y),
        });
      });

      const screenPoints = compactPoints(rawScreenPoints, plotWidth * 2);

      if (!screenPoints.length) return;

      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      screenPoints.forEach((point, index) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });

      if (fill && type === 'area') {
        const gradient = ctx.createLinearGradient(0, chartPadding.top, 0, size.height);
        gradient.addColorStop(0, `${series.color}57`);
        gradient.addColorStop(1, `${series.color}00`);

        ctx.lineTo(screenPoints[screenPoints.length - 1].x, chartPadding.top + plotHeight);
        ctx.lineTo(screenPoints[0].x, chartPadding.top + plotHeight);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      ctx.beginPath();
      screenPoints.forEach((point, index) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      ctx.strokeStyle = series.color;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.stroke();

      if (showMarkers && (showAllMarkers || rawScreenPoints.length <= maxVisibleMarkers)) {
        rawScreenPoints.forEach(point => {
          const markerRadius = markerStrokeColor ? 5 : 4;

          ctx.beginPath();
          ctx.arc(point.x, point.y, markerRadius, 0, Math.PI * 2);
          ctx.fillStyle = series.color;
          ctx.fill();
          ctx.strokeStyle = markerStrokeColor || series.color;
          ctx.lineWidth = 2;
          ctx.stroke();
        });
      }
      ctx.restore();
    });

    if (hover) {
      const x = xScale(hover.timestamp);
      ctx.strokeStyle = '#9CA3AF';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(x, chartPadding.top);
      ctx.lineTo(x, chartPadding.top + plotHeight);
      ctx.stroke();
      ctx.setLineDash([]);

      hover.values.forEach(item => {
        if (!item.point) return;
        const markerRadius = markerStrokeColor ? 6 : 5;

        ctx.beginPath();
        ctx.arc(xScale(item.point.x), yScale(item.point.y), markerRadius, 0, Math.PI * 2);
        ctx.fillStyle = item.color;
        ctx.fill();
        ctx.strokeStyle = markerStrokeColor || item.color;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
  }, [
    bounds,
    countsLabel,
    fill,
    hiddenSeriesKeys,
    hover,
    isMobile,
    markerStrokeColor,
    rotateLabel,
    seriesOpacity,
    showAllMarkers,
    showMarkers,
    size,
    type,
    visibleSeries,
  ]);

  const handleToggleSeries = (seriesKey: string) => {
    const shouldShow = hiddenSeriesKeys.has(seriesKey);
    const nextHiddenKeys = new Set(hiddenSeriesKeys);

    if (shouldShow) {
      nextHiddenKeys.delete(seriesKey);
    } else {
      nextHiddenKeys.add(seriesKey);
    }

    const resolvedHiddenKeys =
      nextHiddenKeys.size === preparedSeries.length ? new Set<string>() : nextHiddenKeys;

    setHiddenSeriesKeys(resolvedHiddenKeys);

    const animationStart = performance.now();
    const startOpacity = shouldShow ? 0 : 1;
    const endOpacity = shouldShow || nextHiddenKeys.size === preparedSeries.length ? 1 : 0;

    const animate = (timestamp: number) => {
      const progress = Math.min((timestamp - animationStart) / legendToggleAnimationDuration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const opacity = startOpacity + (endOpacity - startOpacity) * easedProgress;

      setSeriesOpacity(currentOpacity => ({
        ...currentOpacity,
        [seriesKey]: opacity,
      }));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setSeriesOpacity(currentOpacity => {
          const nextOpacity = { ...currentOpacity };
          delete nextOpacity[seriesKey];
          return nextOpacity;
        });
      }
    };

    requestAnimationFrame(animate);
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!size.width || !size.height) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const plotWidth = Math.max(size.width - chartPadding.left - chartPadding.right, 1);
    const xRatio = Math.min(Math.max((x - chartPadding.left) / plotWidth, 0), 1);
    const timestamp = bounds.minX + (bounds.maxX - bounds.minX) * xRatio;

    isHoverRenderRef.current = true;
    setHover({
      clientX: event.clientX,
      clientY: event.clientY,
      x,
      y,
      timestamp,
      values: visibleSeries.map(series => {
        const point = findNearestPoint(series.data, timestamp);
        return {
          name: series.name,
          currency: series.currency,
          color: series.color,
          value: point?.y ?? null,
          point,
        };
      }),
    });
  };

  const tooltipElement =
    hover && typeof window !== 'undefined'
      ? createPortal(
          <div
            className="pointer-events-none fixed z-[9999] min-w-[250px] rounded-xl bg-white p-4 shadow-lg"
            style={{
              left: Math.min(hover.clientX + 12, Math.max(window.innerWidth - 270, 0)),
              top:
                hover.clientY + 16 + 140 > window.innerHeight
                  ? Math.max(hover.clientY - 156, 8)
                  : Math.max(hover.clientY + 16, 8),
            }}
          >
            <div className="flex items-center justify-between gap-4 text-sm font-medium">
              <span>{new Date(dayjs.utc(hover.timestamp).toISOString()).toDateString()}</span>
              {withDateTime && (
                <span className="text-xs text-gray-500">
                  {dayjs.utc(hover.timestamp).format('HH:mm:ss')}
                </span>
              )}
            </div>

            <div className="mt-2 space-y-2">
              {hover.values.map((item, index) => (
                <div
                  key={`${item.name || 'series'}_${index}`}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="truncate text-sm text-gray-500">{item.name}</span>
                  </div>
                  <span className="text-sm">
                    {item.value !== null
                      ? `${goodFormatNumber(item.value)} ${item.currency || '$'}`
                      : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={classNames('flex min-h-[250px] w-full flex-col', className)} style={{ height }}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-2">
        {preparedSeries.map(series => {
          const isHidden = hiddenSeriesKeys.has(series.key);

          return (
            <button
              key={series.key}
              type="button"
              className={classNames(
                'flex items-center gap-2 rounded text-sm transition-opacity hover:opacity-80',
                isHidden && 'opacity-40',
              )}
              onClick={() => handleToggleSeries(series.key)}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: series.color }} />
              <span className="text-gray-700">{series.name}</span>
            </button>
          );
        })}
      </div>

      <div
        ref={wrapperRef}
        className={classNames(
          'relative min-h-0 flex-1 transition-all duration-700 ease-out',
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        )}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          isHoverRenderRef.current = true;
          setHover(null);
        }}
      >
        <canvas ref={canvasRef} className="block h-full w-full !rounded-none !border-0" />
      </div>
      {tooltipElement}
    </div>
  );
};
